// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartDiscountPercentDisplay from "@guestsupply/App/Components/Cart/CartDiscountPercentDisplay";
import CartShippingSummaryDisplay from "@guestsupply/App/Components/Cart/CartShippingSummaryDisplay";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage, { siteMessageWithCustomParserOptions } from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

import Notify from "@guestsupply/App/Components/Notify";
import xSetCheckoutReviewTerms from "@guestsupply/App/Handlers/XNG/CheckoutReviewTerms";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import PlaceOrderButton from "@guestsupply/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@guestsupply/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { domToReact, HTMLReactParserOptions } from "html-react-parser";
import React, { ChangeEvent, FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updatePickUpWarehouse,
    setFulfillmentMethod,
    // XNG-CHANGES
    preloadCheckoutShippingData,
    xSetCheckoutReviewTerms,
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const billTo = getCurrentBillToState(state).value;
    const shipTo = getCurrentShipToState(state).value;
    const customerSettings = getSettingsCollection(state).customerSettings;

    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        isLoading: cartState.isLoading,
        cart: cartState.value,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showPlaceOrderButton: canPlaceOrder(cartState.value),
        placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
        showSubmitForApprovalOrder: canSubmitForApprovalOrder(cartState.value),
        hasProductsWithInvalidPrice: hasProductsWithInvalidPrice(cartState.value),
        canUpdateShipToAddress:
            billTo?.isGuest ||
            shipTo?.oneTimeAddress ||
            customerSettings.allowBillToAddressEdit ||
            customerSettings.allowShipToAddressEdit,
        // XNG-CHANGES
        cartId: cartState.value?.isAwaitingApproval ? cartState.value?.id : undefined,
        isBackButtonDisabled: state.pages.checkoutReviewAndSubmit.isPlacingOrder,
        checkoutShippingPageLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        isChecked: (state as CustomAppState).xCheckoutReviewTerms.isChecked,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutReviewAndSubmitCartTotalStyles {
    container?: InjectableCss;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    buttonsGridItem?: GridItemProps;
    hasInsufficientInventoryGridItem?: GridItemProps;
    availabilityErrorMessageText?: TypographyPresentationProps;
    placeOrderErrorMessageGridItem?: GridItemProps;
    placeOrderErrorMessageText?: TypographyPresentationProps;
    invalidEditableAddressErrorMessageGridItem?: GridItemProps;
    invalidEditableAddressErrorMessageText?: TypographyPresentationProps;
    invalidAddressErrorMessageGridItem?: GridItemProps;
    invalidAddressErrorMessageText?: TypographyPresentationProps;
    invalidPriceErrorMessageGridItem?: GridItemProps;
    invalidPriceErrorMessageText?: TypographyPresentationProps;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    defaultFulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    defaultFulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    // XNG-CHANGES
    backButton?: ButtonPresentationProps;
    tooltipStyles?: TooltipPresentationProps;
    notifyStyles?: TypographyProps;
    buttonsWrapper?: ButtonPresentationProps;
    notifyWrapper?: InjectableCss;
}

export const cartTotalStyles: CheckoutReviewAndSubmitCartTotalStyles = {};

const styles = cartTotalStyles;

const CheckoutReviewAndSubmitCartTotal: FC<Props> = ({
    showPlaceOrderButton,
    cart,
    isLoading,
    shippingPromotions,
    discountTotal,
    orderPromotions,
    placeOrderErrorMessage,
    updatePickUpWarehouse,
    setFulfillmentMethod,
    showSubmitForApprovalOrder,
    canUpdateShipToAddress,
    hasProductsWithInvalidPrice,
    // XNG-CHANGES
    isBackButtonDisabled,
    cartId,
    preloadCheckoutShippingData,
    checkoutShippingPageLink,
    history,
    isChecked,
    xSetCheckoutReviewTerms,
}) => {
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const [showTermsAndConditionsErrorMessage, setShowTermsAndConditionsErrorMessage] = React.useState(false);

    const openWarehouseSelectionModal = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const toasterContext = React.useContext(ToasterContext);
    const handleWarehouseSelected = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
        setIsFindLocationOpen(false);
    };

    const [isFulfillmentMethodOpen, setIsFulfillmentMethodOpen] = React.useState(false);
    const openDeliveryMethodPopup = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFulfillmentMethodOpen(true);
    };

    const modalCloseHandler = () => {
        setIsFulfillmentMethodOpen(false);
    };

    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
    };

    const parserOptions: HTMLReactParserOptions = {
        replace: node => {
            const { name, children, attribs = { style: "" }, ...rest } = node;
            const { style, class: className, ...otherAttribs } = attribs;

            otherAttribs.className = className;

            if (name === "a") {
                let onClickHandler: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
                for (const key in otherAttribs) {
                    if (key === "ng-click") {
                        if (otherAttribs[key] === "vm.openWarehouseSelectionModal()") {
                            onClickHandler = openWarehouseSelectionModal;
                        }
                        if (otherAttribs[key] === "vm.openDeliveryMethodPopup()") {
                            onClickHandler = openDeliveryMethodPopup;
                        }
                    }
                }
                return (
                    <Link
                        css={style as any}
                        id={otherAttribs.name}
                        {...otherAttribs}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                            onClickHandler && onClickHandler(event)
                        }
                    >
                        {children && domToReact(children, parserOptions)}
                    </Link>
                );
            }
        },
    };

    // XNG-CHANGES
    const backClickHandler = () => {
        history.push(checkoutShippingPageLink!.url);
        // preloadCheckoutShippingData({
        //     cartId,
        //     onSuccess: () => {
        //         const backUrl = cartId
        //             ? `${checkoutShippingPageLink!.url}?cartId=${cartId}`
        //             : checkoutShippingPageLink!.url;
        //     },
        // });
    };

    const checkBoxChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        xSetCheckoutReviewTerms({ isChecked: value });
    };

    return (
        <StyledWrapper {...styles.container}>
            <CartTotalDisplay
                cart={cart}
                isLoading={isLoading}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
                orderPromotions={orderPromotions}
            />
            <CartShippingSummaryDisplay
                extendedStyles={styles.cartTotal}
                cart={cart}
                // {...{  showSeparateShippingAndHandling: true }}
            />
            <CartDiscountPercentDisplay
                extendedStyles={styles.cartTotal}
                cart={cart}
                // {...{ showSeparateShippingAndHandling: true }}
            />
            <StyledWrapper>
                <Checkbox checked={isChecked} onChange={checkBoxChangeHandler}>
                    <Tooltip
                        {...styles.tooltipStyles}
                        text={siteMessage("Guest_Terms_And_Conditions_Msg", "Guest_Terms_And_Conditions_Msg")}
                        triggerComponent={<>{translate("Accept Terms & Conditions")}</>}
                    />
                </Checkbox>
            </StyledWrapper>
            {cart && cart.hasInsufficientInventory === true && (
                <StyledWrapper>
                    <Typography
                        {...styles.availabilityErrorMessageText}
                        data-test-selector="checkoutReviewAndSubmit_availabilityErrorMessage"
                    >
                        {siteMessageWithCustomParserOptions("ReviewAndPay_NotEnoughInventoryForPickup", parserOptions)}
                    </Typography>
                    <FindLocationModal
                        modalIsOpen={isFindLocationOpen}
                        onWarehouseSelected={handleWarehouseSelected}
                        onModalClose={handleFindLocationModalClose}
                        extendedStyles={styles.findLocationModal}
                    />
                    <Modal
                        size={500}
                        headline={translate("Fulfillment Method")}
                        isOpen={isFulfillmentMethodOpen}
                        handleClose={modalCloseHandler}
                    >
                        <RadioGroup
                            value={cart.fulfillmentMethod}
                            onChangeHandler={fulfillmentMethodChangeHandler}
                            {...styles.defaultFulfillmentMethodRadioGroup}
                        >
                            <Radio value={FulfillmentMethod.Ship} {...styles.defaultFulfillmentMethodRadio}>
                                {translate("Ship")}
                            </Radio>
                            <Radio value={FulfillmentMethod.PickUp} {...styles.defaultFulfillmentMethodRadio}>
                                {translate("Pick Up")}
                            </Radio>
                        </RadioGroup>
                    </Modal>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.notifyWrapper}>
                {!isChecked && showTermsAndConditionsErrorMessage && (
                    <Notify
                        {...{ fullWidth: true }}
                        type="danger"
                        data-test-selector="CheckoutReviewAndSubmitPage_TermsAndConditions"
                        {...styles.notifyStyles}
                    >
                        {siteMessage("Guest_Accept_Terms_Msg", "You should accept terms and condition")}
                    </Notify>
                )}
                {placeOrderErrorMessage && (
                    <Notify
                        {...{ fullWidth: true }}
                        type="danger"
                        data-test-selector="checkoutReviewAndSubmit_placeOrderErrorMessage"
                        {...styles.notifyStyles}
                    >
                        {placeOrderErrorMessage}
                    </Notify>
                )}
                {cart?.taxFailureReason === "InvalidAddress" && canUpdateShipToAddress && (
                    <Notify {...{ fullWidth: true }} type="danger" {...styles.notifyStyles}>
                        {siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")}
                    </Notify>
                )}
                {cart?.taxFailureReason === "InvalidAddress" && !canUpdateShipToAddress && (
                    <Notify {...{ fullWidth: true }} type="danger" {...styles.notifyStyles}>
                        {siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}
                    </Notify>
                )}
                {hasProductsWithInvalidPrice && (
                    <Notify {...{ fullWidth: true }} type="danger" {...styles.notifyStyles}>
                        <StyledWrapper
                            css={css`
                                display: flex;
                                flex-wrap: wrap;
                            `}
                        >
                            {siteMessage("ReviewAndPay_HasProductsWithInvalidPrice")}
                        </StyledWrapper>
                    </Notify>
                )}
            </StyledWrapper>
            {/* XNG-CHANGES : Moved to the buttom */}
            <StyledWrapper {...styles.buttonsWrapper}>
                {cart && showPlaceOrderButton && (
                    <PlaceOrderButton
                        isChecked={isChecked}
                        setShowTermsAndConditionsErrorMessage={setShowTermsAndConditionsErrorMessage}
                        styles={styles.placeOrderButton}
                    />
                )}
                {cart && showSubmitForApprovalOrder && (
                    <SubmitForApprovalButton
                        isChecked={isChecked}
                        setShowTermsAndConditionsErrorMessage={setShowTermsAndConditionsErrorMessage}
                        extendedStyles={styles.submitForApprovalButton}
                    />
                )}
                <Button
                    {...styles.backButton}
                    onClick={backClickHandler}
                    disabled={isBackButtonDisabled}
                    data-test-selector="checkoutReviewAndSubmit_backButton"
                >
                    {translate("Previous")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitCartTotal)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Cart Total",
    },
};

export default widgetModule;
